<div class="col">
  <!-- Work Order Summaries Section -->
  <p-panel header="Work Order Summary by Color">
    <p-card data-variant="compact">
      <div class="overflow-x-scroll">
        <p-table [value]="WorkOrderSummaries" class="table w-full" [scrollable]="true">
          <ng-template #header>
            <tr class="dark__header">
              <th class="text-center whitespace-nowrap">Color</th>
              <th class="text-center whitespace-nowrap">Client PO</th>
              <th class="text-center whitespace-nowrap">Fabric Code</th>
              <th class="text-center whitespace-nowrap">Description</th>
              <th class="text-center whitespace-nowrap">Loop Length(s)</th>
              <th class="text-center whitespace-nowrap">GSM</th>
              <th class="text-center whitespace-nowrap">Width</th>
              <th class="text-center whitespace-nowrap">Needles</th>
              <th class="text-center whitespace-nowrap">Required Qty</th>
              <th class="text-center whitespace-nowrap">Knitting Qty</th>
              <th class="text-center whitespace-nowrap">Yarn Qty</th>
            </tr>
          </ng-template>

          <ng-template #body let-summary let-rowIndex="rowIndex">
            <!-- Main row for the color summary -->
            @if (summary.fabricEntries.length > 0) {
              @for (entry of summary.fabricEntries; track $index; let first = $first) {
                <tr>
                  @if (first) {
                    <td [rowSpan]="summary.fabricEntries.length" class="text-center align-top">
                      <div class="flex flex-col items-center gap-2">
                        <div class="w-10 h-8 rounded border border-gray-300"></div>
                        <span class="text-sm">{{ summary.color.name }}</span>
                        @if (summary.color.approvedRecipeNumber) {
                          <span class="text-xs text-gray-500">{{ summary.color.approvedRecipeNumber }}</span>
                        }
                      </div>
                    </td>
                    <td [rowSpan]="summary.fabricEntries.length" class="text-center align-top">
                      {{ summary.clientProductOrderNumber }}
                    </td>
                  }
                  <td class="text-center">
                    <div class="flex flex-col items-center">
                      <span>{{ entry.fabricCode }}</span>
                      @if (entry.greigeCode) {
                        <span class="text-xs text-gray-500">{{ entry.greigeCode }}</span>
                      }
                    </div>
                  </td>
                  <td>{{ entry.description }}</td>
                  <td class="text-center">{{ getLoopLengths(entry.fabricCode) }}</td>
                  <td class="text-center">{{ entry.gsm | number: '1.0-2' }}</td>
                  <td class="text-center">{{ entry.width | number: '1.0-2' }}</td>
                  <td class="text-center">{{ getNeedles(entry.fabricCode) }}</td>
                  <td class="text-center">{{ entry.requiredQuantity | number: '1.0-2' }}</td>
                  <td class="text-center cursor-help" tooltipPosition="top" [style.white-space]="'pre-line'">
                    {{ entry.knittingQuantity | number: '1.0-2' }}
                  </td>
                  <td class="text-center cursor-help" tooltipPosition="top" [style.white-space]="'pre-line'">
                    {{ entry.yarnQuantity | number: '1.0-2' }}
                  </td>
                </tr>
              }
              <!-- Totals row -->
              <tr class="font-bold bg-gray-50">
                <td colspan="8" class="text-right">Total:</td>
                <td class="text-center">{{ summary.totalRequiredQuantity | number: '1.0-2' }}</td>
                <td class="text-center">{{ summary.totalKnittingQuantity | number: '1.0-2' }}</td>
                <td class="text-center">{{ summary.totalYarnQuantity | number: '1.0-2' }}</td>
              </tr>
            } @else {
              <tr>
                <td colspan="11" class="text-center">No fabric entries</td>
              </tr>
            }
          </ng-template>

          <ng-template #emptymessage>
            <tr>
              <td colspan="11" class="text-center">No work order summaries available</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </p-card>
  </p-panel>

  <!-- Yarn Requirements Section -->
  <p-panel header="Yarn Requirements">
    <p-card data-variant="compact">
      <p-table [value]="YarnRequirements" class="table w-full" [scrollable]="true">
        <ng-template #header>
          <tr class="dark__header">
            <th class="text-center">Item Code</th>
            <th class="text-center">PD Yarn Codes</th>
            <th class="text-center">Description</th>
            <th class="text-center">Total Quantity</th>
            <th class="text-center whitespace-nowrap">
              {{ 'workOrderSummary.percentageOfTotalQuantity' | translateWF: 'Percentage of Total Quantity' }}
            </th>
            <th class="text-center">Fabric Breakdown</th>
          </tr>
        </ng-template>

        <ng-template #body let-yarnReq>
          <tr>
            <td class="text-center">
              @if (yarnReq.itemCode) {
                <span class="font-semibold">{{ yarnReq.itemCode }}</span>
              } @else {
                <span class="text-gray-400 italic">Not Created</span>
              }
            </td>
            <td class="text-center">
              <div class="flex flex-wrap gap-1 justify-center">
                @for (pdYarnCode of yarnReq.pdYarnCodes; track pdYarnCode) {
                  <p-tag [value]="pdYarnCode" severity="info" />
                }
              </div>
            </td>
            <td>{{ yarnReq.description }}</td>
            <td class="text-center font-semibold">
              {{ yarnReq.totalQuantity | number: '1.0-2' }}
            </td>
            <td class="text-center">{{ yarnReq.percentageOfTotalQuantity | number: '1.2-2' }}%</td>
            <td>
              <div class="flex flex-col gap-1">
                @for (fabricQty of yarnReq.fabricQuantities; track fabricQty.description) {
                  <div class="flex justify-between items-center px-2 py-1 bg-gray-50 rounded">
                    <span class="text-sm">{{ fabricQty.description }}</span>
                    <span class="text-sm font-semibold ml-4">{{ fabricQty.quantity | number: '1.0-2' }}</span>
                  </div>
                }
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template #emptymessage>
          <tr>
            <td colspan="6" class="text-center">No yarn requirements available</td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>
  </p-panel>

  <!-- Available Inventory Section -->
  <p-panel
    header="{{
      'workOrderSummary.availableInventoryRawMaterial'
        | translateWF
          : 'Available inventory in Raw
    Material
    Warehouse'
    }}"
  >
    <p-card data-variant="compact">
      <p-table [value]="availableInventory()" class="table w-full" [scrollable]="true" [loading]="isLoading() > 0">
        <ng-template #header>
          <tr class="dark__header">
            <th class="text-center">{{ 'workOrderSummary.yarnCode' | translateWF: 'Yarn Code' }}</th>
            <th class="text-center">{{ 'workOrderSummary.description' | translateWF: 'Description' }}</th>
            <th class="text-center">{{ 'workOrderSummary.manufacturer' | translateWF: 'Manufacturer' }}</th>
            <th class="text-center">{{ 'workOrderSummary.lot' | translateWF: 'Lot' }}</th>
            <th class="text-center">{{ 'workOrderSummary.lotItemCount' | translateWF: 'Lot Items' }}</th>
            <th class="text-center">{{ 'workOrderSummary.pounds' | translateWF: 'Pounds' }}</th>
          </tr>
        </ng-template>

        <ng-template #body let-group>
          <tr>
            <td class="text-center font-semibold">{{ group.itemCode }}</td>
            <td>{{ group.itemDisplayName | localized }}</td>
            <td class="text-center">{{ group.manufacturer?.name || '—' }}</td>
            <td class="text-center">{{ group.manufacturerLotNumber || '—' }}</td>
            <td class="text-center">{{ group.lotItemCount | number }}</td>
            <td class="text-center">{{ group.totalNetQuantity | number: '1.0-2' }}</td>
          </tr>
        </ng-template>

        <ng-template #footer>
          @if (availableInventory().length > 0) {
            <tr class="font-bold bg-gray-50">
              <td colspan="4" class="text-right">Total:</td>
              <td class="text-center">{{ totalAvailableLotItems() | number }}</td>
              <td class="text-center">{{ totalAvailableQuantity() | number: '1.0-2' }}</td>
            </tr>
          }
        </ng-template>

        <ng-template #emptymessage>
          <tr>
            <td colspan="6" class="text-center">
              {{ 'workOrderSummary.noAvailableInventory' | translateWF: 'No available inventory for these yarns' }}
            </td>
          </tr>
        </ng-template>
      </p-table>
    </p-card>
  </p-panel>
</div>
